import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { LabelTemplate, LabelTemplateElement } from '../types/printing';

export function useLabelTemplates() {
  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: queryError } = await supabase
        .from('label_templates')
        .select('*')
        .order('is_default', { ascending: false })
        .order('name');

      if (queryError) throw queryError;
      setTemplates((data as LabelTemplate[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch templates'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const createTemplate = async (template: {
    name: string;
    description?: string;
    width_dots: number;
    height_dots: number;
    rotation?: number;
    elements: LabelTemplateElement[];
    is_default?: boolean;
  }) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('label_templates')
      .insert({
        name: template.name,
        description: template.description || null,
        width_dots: template.width_dots,
        height_dots: template.height_dots,
        rotation: template.rotation || 0,
        elements: template.elements as unknown as Record<string, unknown>,
        is_default: template.is_default ?? false,
        created_by: user?.id ?? null,
      })
      .select()
      .single();

    if (error) throw error;
    await fetchTemplates();
    return data as LabelTemplate;
  };

  const updateTemplate = async (
    id: string,
    updates: Partial<{
      name: string;
      description: string | null;
      width_dots: number;
      height_dots: number;
      rotation: number;
      elements: LabelTemplateElement[];
      is_default: boolean;
    }>
  ) => {
    const updateData: Record<string, unknown> = {};
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.width_dots !== undefined) updateData.width_dots = updates.width_dots;
    if (updates.height_dots !== undefined) updateData.height_dots = updates.height_dots;
    if (updates.rotation !== undefined) updateData.rotation = updates.rotation;
    if (updates.elements !== undefined) updateData.elements = updates.elements;
    if (updates.is_default !== undefined) updateData.is_default = updates.is_default;

    const { data, error } = await supabase
      .from('label_templates')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    await fetchTemplates();
    return data as LabelTemplate;
  };

  const deleteTemplate = async (id: string) => {
    const { error } = await supabase.from('label_templates').delete().eq('id', id);
    if (error) throw error;
    await fetchTemplates();
  };

  const duplicateTemplate = async (id: string, newName: string) => {
    const source = templates.find(t => t.id === id);
    if (!source) throw new Error('Template not found');

    return createTemplate({
      name: newName,
      description: source.description || undefined,
      width_dots: source.width_dots,
      height_dots: source.height_dots,
      rotation: source.rotation,
      elements: source.elements,
    });
  };

  return {
    templates,
    loading,
    error,
    refetch: fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
  };
}
